// @ts-nocheck
import { useDispatch } from 'react-redux';
import { showNotification, useQueryFetch } from 'sonata-react-components';

import { setPdfStatusChanged } from '@/store/actions/reporting.actions';
import { downloadPDFReport } from '@/ts-resources/reporting/pdfReport.resources';
import { ReportingTemplateRequest } from '@/ts-resources/templates/requests/ReportingTemplate.request';

import type {
  GraphSerie,
  GraphType
} from 'sonata-react-components/src_ts/components/InsightsChart/insightsChart.types';

export type InsightItem = {
  chartType?: 'genderAgeColumn' | 'pie' | 'stackedBar';
  colorAxis?: {
    maxColor: string;
    minColor: string;
  };
  colorByLabel?: Record<string, string>;
  colors?: string[];
  extraInfo?: string;
  filterOptions: Record<string, any>;
  groupsOptions: {
    groupBy: string;
    others: boolean;
    size: number;
  }[];
  id: string;
  permissions: string[];
  showProgressBar: boolean;
  title: string;
  variant: string;
};

type InsightFamily = {
  items: InsightItem[];
  label: string;
  type: string;
};

type SummaryNormalizedItems = {
  bottomMetricLabels: Record<string, any>[];
  color: string;
  goal: string;
  goalCurrent: number;
  goalTarget: number;
  graph: {
    config: Record<string, any>;
    graphId: string;
  };
  icon: string;
  key: string;
  text: string;
  trendDown: boolean;
  trendUp: boolean;
  value: string;
};

type GraphConfig = {
  chart: {
    backgroundColor: string;
    height: number;
    type: GraphType;
  };
  colors: string[];
  credit: {
    enabled: boolean;
  };
  plotOptions: Record<string, any>;
  series: GraphSerie[];
  xAxis: Record<string, any>;
  yAxis: Record<string, any>[];
};

type Props = {
  campaignProgressConfig: {
    campaignNames: string[];
    dailyGraphConfig: GraphConfig;
    graphConfig: GraphConfig;
    hourlyGraphConfig: GraphConfig;
    kpi: {
      id: string;
      label: string;
    };
    metric: {
      id: string;
      label: string;
    };
    totalTagsHeight: number;
  };
  campaignsIds: string[];
  cartographyMapConfig?: {
    opts: {
      currencyOptions: {
        currency: string;
      };
    };
    params: {
      dateInterval: {
        beginDate: string;
        endDate: string;
        timezone: string;
      };
      filters: {
        cartography: Record<string, string[]>;
      };
      level?: string;
      metric: string;
      metricLabel: string;
    };
  };
  country?: string;
  filters: Record<string, any>;
  id: string;
  insightsConfig: {
    families: InsightFamily[];
    general: {
      metricsConfig: {
        kpi?: {
          id: string;
          label: string;
        };
        metric: {
          id: string;
          label: string;
        };
      };
      opts: {
        currencyOptions: {
          currency: string;
        };
      };
      params: {
        cartography?: Record<string, any>;
        dateInterval: {
          beginDate: string;
          endDate: string;
          timezone: string;
        };
        filters?: Record<string, any>;
      };
    };
  };
  isEnabled?: boolean;
  omits?: {
    aiInsights?: boolean;
  };
  requestInit: ReportingTemplateRequest;
  summaryConfig: {
    performance: Record<string, any>;
    summaryNormalized: {
      isReady: boolean;
      items?: SummaryNormalizedItems[];
    };
  };
  userId: string;
};

export function useReportPDFDownloadRequest({
  campaignProgressConfig,
  campaignsIds,
  cartographyMapConfig,
  country,
  filters,
  id,
  insightsConfig,
  isEnabled,
  omits,
  requestInit,
  summaryConfig,
  userId
}: Props) {
  const dispatch = useDispatch();
  const { isFetching, isLoading, refetch } = useQueryFetch(
    ['downloadPDFReport', campaignsIds, cartographyMapConfig, filters, isEnabled, id],
    async () => {
      try {
        const report = await downloadPDFReport(userId, campaignsIds, {
          ...requestInit.toRequest(),
          campaignProgressConfig,
          cartographyMapConfig,
          country,
          filters,
          insightsConfig,
          omits,
          summaryConfig,
          type: id
        });
        dispatch(
          setPdfStatusChanged({
            key: id,
            value: report
          })
        );
      } catch {
        showNotification('error', 'Error creating PDF, please try again later');
      }
    },
    {
      enabled: false
    }
  );

  return {
    generateReport: () => refetch(),
    isLoading: isFetching || isLoading
  };
}
